import { NativeDateAdapter } from '@angular/material/core';
import { Injectable, inject, DestroyRef } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export class DataFormatters {

  /**
   * Converts a Date object to a YYYY-MM-DD string, respecting the local timezone.
   *
   * This method uses the local date components (getFullYear, getMonth, getDate)
   * to prevent the timezone conversion issues that occur with `toISOString()`.
   *
   * @param {date} date The Date object to format. Handles falsy values (null, undefined) by returning an empty string.
   * @return {string} The formatted date string (e.g., "2025-11-07") or an empty string if the input date is falsy.
   */
  public static toLocalYYYYMMDD(date: Date): string {

    if (!date) return ''

    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Determines the appropriate store code to display on the UI based on its content.
   * If the store code contains at least one letter (e.g., 'GT001'), it returns the original store code.
   * If the store code only contains numbers (e.g., '00003'), it falls back to the numeric code.
   *
   * @param {string | undefined} storeCode - The primary alphanumeric or numeric store code.
   * @param {number} storeNumericCode - The fallback numeric code for the store.
   * @return {string} The correct display code for the store.
   */
  public static getDisplayCode(storeCode: string | undefined, storeNumericCode: number): string | number {
    const hasLetters = /[a-zA-Z]/.test(storeCode || '')
    return hasLetters ? (storeCode || '') : storeNumericCode
  }

  /**
   * Returns the appropriate date format string based on the current locale.
   *
   * @param {string} locale - The current locale string.
   * @return {string} The date format string.
   */
  public static readonly getCustomDateFormat = (locale: string): string => {

    if (!locale) {
      return 'dd/MM/yyyy';
    }

    const normalizedLocale = locale.toLowerCase();

    if (normalizedLocale.startsWith('en')) {
      return 'MM/dd/yyyy';
    }

    return 'dd/MM/yyyy';
  };

}


/**
 * Custom date adapter that formats dates according to the current locale.
 *
 * This adapter uses the Intl.DateTimeFormat API to format dates, which is more
 * accurate than the default Angular Material date adapter.
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (!this.isValid(date)) {
      return super.format(date, displayFormat as NonNullable<unknown>);
    }

    return new Intl.DateTimeFormat(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }
}

/**
 * Custom paginator intl that uses the translate service to format the text.
 *
 * This class extends the default MatPaginatorIntl class and overrides the
 * getRangeLabel method to use the translate service to format the text.
 */
@Injectable()
export class CustomPaginatorIntl extends MatPaginatorIntl {

  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateTranslations();
      });

    this.updateTranslations();
  }

  private updateTranslations(): void {
    this.itemsPerPageLabel = this.translateService.instant('sales.reports.common.paginator.itemsPerPage');
    this.nextPageLabel = this.translateService.instant('sales.reports.common.paginator.nextPage');
    this.previousPageLabel = this.translateService.instant('sales.reports.common.paginator.previousPage');
    this.firstPageLabel = this.translateService.instant('sales.reports.common.paginator.firstPage');
    this.lastPageLabel = this.translateService.instant('sales.reports.common.paginator.lastPage');

    this.changes.next();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const ofText = this.translateService.instant('sales.reports.common.paginator.of')

    if (length === 0 || pageSize === 0) {
      return `0 ${ofText} 0`;
    }

    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex = startIndex < length
      ? Math.min(startIndex + pageSize, length)
      : startIndex + pageSize;

    return `${startIndex + 1} – ${endIndex} ${ofText} ${length}`;
  };
}
